import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import {Box, Button, CssBaseline, GlobalStyles, Stack, ThemeProvider, Typography} from '@mui/material';
import {ManualArticle, getFirstSlug, getPages} from '@area-monitoring/am-app-manual';
import {createFileRoute, useNavigate} from '@tanstack/react-router';
import {createPlanetTheme} from '@apps-common/ui-theme/Themes';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';

const printTheme = createPlanetTheme({
  colorScheme: 'light',
  useCssVars: false,
});

// Only reached via client-side navigation, so it always renders here instead of the AM app.
const PrintComponent: React.FC = () => {
  const {i18n, t} = useTranslation();
  const navigate = useNavigate();
  const pages = getPages(i18n.language);
  const hasContent = pages.length > 0;

  useEffect(() => {
    if (!hasContent) {
      return;
    }

    let cancelled = false;
    const images = Array.from(document.images);
    const settled = images.map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>(resolve => {
            img.addEventListener('load', () => resolve(), {once: true});
            img.addEventListener('error', () => resolve(), {once: true});
          }),
    );
    void Promise.all(settled).then(() => {
      if (!cancelled) {
        window.print();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [hasContent]);

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'html, body': {background: '#fff !important'},
            '.manual-print-toolbar': {display: 'none !important'},
            '.manual-print-root, .manual-print-root *': {
              color: '#000 !important',
              borderColor: '#ccc !important',
            },
            '.manual-print-root a': {color: '#000 !important', textDecoration: 'underline'},
            '.manual-print-root pre, .manual-print-root code': {
              background: '#f4f4f4 !important',
            },
            '.manual-print-root img': {maxWidth: '100% !important', height: 'auto'},
            '.manual-print-chapter': {breakBefore: 'page', pageBreakBefore: 'always'},
            '.manual-print-chapter:first-of-type': {breakBefore: 'auto', pageBreakBefore: 'auto'},
          },
        }}
      />
      <Stack
        className="manual-print-toolbar"
        direction="row"
        spacing={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          disabled={!hasContent}
          onClick={() => {
            const slug = getFirstSlug(i18n.language);
            if (slug) {
              void navigate({to: '/$slug', params: {slug}});
            }
          }}
        >
          {t('back-to-manual')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PrintOutlinedIcon />}
          disabled={!hasContent}
          onClick={() => window.print()}
        >
          {t('download-pdf')}
        </Button>
      </Stack>
      <ThemeProvider theme={printTheme}>
        <CssBaseline enableColorScheme />
        <Box sx={{minHeight: '100vh', bgcolor: 'background.default'}}>
          <Box className="manual-print-root" sx={{maxWidth: 920, mx: 'auto', p: 4}}>
            <Box sx={{textAlign: 'center', mb: 6}}>
              <Typography variant="h3" sx={{fontWeight: 700}}>
                {t('app-title')}
              </Typography>
            </Box>
            {hasContent ? (
              pages.map(page => {
                const Content = page.Component;
                return (
                  <Box key={page.slug} className="manual-print-chapter">
                    <ManualArticle>
                      <Content />
                    </ManualArticle>
                  </Box>
                );
              })
            ) : (
              <Typography variant="h6" sx={{textAlign: 'center'}}>
                {t('no-manual-content')}
              </Typography>
            )}
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
};

export const Route = createFileRoute('/print')({
  component: PrintComponent,
});
